export const templates = [
  {
    id: 'story-mode',
    name: 'Story Mode',
    description: 'Create a 6-panel illustrated story for children',
    hasStoryMode: true,
    getPrefix: (storyPrompt, panelNumber) => {
      const baseStyle = "Create a child-friendly cartoon illustration in a Disney/Pixar animation style with bright colors and clear details. The image should be cheerful and engaging for children.";
      
      const storyStructure = [
        `${baseStyle} For the opening scene of the story about ${storyPrompt}, show the main characters and setting being introduced. Make it welcoming and establish the mood.`,
        
        `${baseStyle} For the second scene of the story about ${storyPrompt}, show the beginning of the adventure or challenge the characters face. Include interesting details that move the story forward.`,
        
        `${baseStyle} For the middle of the story about ${storyPrompt}, show the main conflict or challenge reaching its peak. Make the scene dramatic but not scary.`,
        
        `${baseStyle} For the turning point of the story about ${storyPrompt}, show how the characters work to overcome their challenge. Include elements of teamwork or clever problem-solving.`,
        
        `${baseStyle} For the story climax about ${storyPrompt}, show the exciting moment when the solution comes together. Make it triumphant and uplifting.`,
        
        `${baseStyle} For the happy ending of the story about ${storyPrompt}, show the characters celebrating their success or sharing a heartwarming moment. Include elements that wrap up the story nicely.`
      ];

      return storyStructure[panelNumber - 1];
    },
    example: 'a young wizard discovering they have magical powers',
    placeholder: 'Describe your story idea (e.g., "A friendly dragon who learns to bake cookies", "A space cat exploring new planets")'
  },
  {
    id: 'watercolor',
    name: 'Watercolor',
    description: 'Soft, artistic watercolor style',
    prefix: 'Create a watercolor style poster with soft, flowing colors and artistic brush strokes of',
    example: 'a mountain landscape',
    placeholder: 'Describe what you want painted in watercolor (e.g., "a serene mountain lake at sunset")'
  },
  {
    id: 'vintage-travel',
    name: 'Vintage Travel',
    description: 'Classic travel poster style from the 1930s-1950s',
    prefix: 'Design a vintage travel poster in the style of 1940s advertisement posters of',
    example: 'Paris with the Eiffel Tower',
    placeholder: 'Enter a location or destination (e.g., "New York City with the Empire State Building")'
  },
  {
    id: 'minimalist',
    name: 'Minimalist',
    description: 'Clean, simple designs with bold elements',
    prefix: 'Create a minimalist poster design with clean lines and simple shapes showing',
    example: 'a sunset over the ocean',
    placeholder: 'Describe a simple scene or object (e.g., "a single tree on a hill against a full moon")'
  },
  {
    id: 'line-art',
    name: 'Line Art',
    description: 'Simple black and white line drawings',
    hasAgeSlider: true,
    getPrefix: (age, prompt) => {
      const basePrompt = `Create a black and white line drawing of ${prompt}. Make sure the background is pure white and the entire drawing fits within the frame with proper margins. Ensure the composition is zoomed out enough to show the complete subject.`;
      
      if (age === 18) {
        return `${basePrompt} Use complex outlining with variable line widths suitable for a professional artist in training. Include intricate details and patterns while maintaining clear boundaries. Avoid realistic rendering or 3D effects.`;
      } else if (age >= 13) {
        return `${basePrompt} Use varied line weights and some shading, suitable for a teenager to colour in. Include moderately complex elements and patterns while ensuring the entire drawing is visible. Avoid realistic rendering or 3D effects.`;
      } else if (age >= 11) {
        return `${basePrompt} Use clear outlines and simple shading, suitable for a ${age}-year-old child to colour in. The drawing should be engaging but not too complex. No realistic or 3D elements.`;
      } else if (age >= 6) {
        return `${basePrompt} Use clear outlines and minimal shading, suitable for a ${age}-year-old child to colour in. Keep the drawing simple and easy to understand. No realistic renderings or 3D effects.`;
      } else {
        return `${basePrompt} Use very simple, thick black outlines of consistent width, suitable for a ${age}-year-old child to colour in. Make the drawing extremely simple and clean with NO shading or detailed elements. Avoid any realistic or 3D effects.`;
      }
    },
    example: 'a city skyline',
    placeholder: 'Describe what you want drawn in line art (e.g., "a detailed botanical illustration of a rose")'
  },
  {
    id: 'retro',
    name: 'Retro',
    description: '1980s-style retro aesthetic',
    prefix: 'Design a retro-style poster with 1980s aesthetics, neon colors, and grid patterns featuring',
    example: 'a sports car',
    placeholder: 'Describe something for a retro poster (e.g., "a synthwave sunset with a DeLorean car")'
  },
  {
    id: 'movie',
    name: 'Movie Poster',
    description: 'Dramatic movie poster style',
    prefix: 'Create a dramatic movie poster style image with cinematic lighting and composition showing',
    example: 'a superhero in action',
    placeholder: 'Describe a movie-worthy scene (e.g., "a lone astronaut floating in space with Earth in the background")'
  }
];

export const getTemplatePrompt = (templateId, userPrompt, age = null, panelNumber = null, storyData = null) => {
  const template = templates.find(t => t.id === templateId);
  if (!template) return userPrompt;
  
  if (template.hasStoryMode && panelNumber !== null) {
    if (storyData && storyData.chapters[panelNumber - 1]) {
      const chapter = storyData.chapters[panelNumber - 1];
      return `Create a child-friendly cartoon illustration in a Disney/Pixar animation style with bright colors and clear details. The image should be cheerful and engaging for children. Scene description: ${chapter.scene}`;
    }
    return template.getPrefix(userPrompt, panelNumber);
  }
  
  if (template.hasAgeSlider && age !== null) {
    return template.getPrefix(age, userPrompt);
  }
  
  return `${template.prefix} ${userPrompt}. Make sure all text is visible and the composition is zoomed out enough to show all important elements clearly. Ensure proper spacing and layout for a poster design.`;
};

export const generateStoryText = (storyData, panelNumber) => {
  if (storyData && storyData.chapters[panelNumber - 1]) {
    return storyData.chapters[panelNumber - 1].text;
  }
  
  // Fallback to default story beats
  const storyBeats = [
    `Once upon a time, ${storyData.title}. The adventure was about to begin...`,
    `As our story continues, our heroes face their first challenge...`,
    `Things become more complicated as ${storyData.title.toLowerCase()}...`,
    `But then, something unexpected happens...`,
    `With courage and determination, our heroes find a way...`,
    `And finally, everyone learns that ${storyData.title.toLowerCase()}. The End.`
  ];
  return storyBeats[panelNumber - 1];
}; 